import { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Package, Search, CheckCircle2, Truck, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://kiyafet-magazasi-backend.vercel.app';

export function OrderTracking() {
  const [trackingInput, setTrackingInput] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trackingInput.trim()) { toast.error('Lütfen sipariş numaranızı giriniz'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/kargo-takip/${trackingInput.trim()}`);
      if (res.ok) {
        const data = await res.json();
        setOrderData(data);
        setShowResults(true);
        toast.success('Sipariş bulundu!');
      } else {
        toast.error('Sipariş bulunamadı');
      }
    } catch { toast.error('Bağlantı hatası'); }
    setLoading(false);
  };

  const getDurumText = (durum: string) => {
    switch (durum) {
      case 'teslim_edildi': return 'Teslim Edildi';
      case 'kargoda': return 'Kargoda';
      case 'dagitimda': return 'Dağıtımda';
      case 'hazirlaniyor': return 'Hazırlanıyor';
      case 'iptal': return 'İptal';
      default: return 'Beklemede';
    }
  };

  const getSteps = (durum: string) => {
    const allSteps = ['Sipariş Alındı', 'Hazırlanıyor', 'Kargoya Verildi', 'Dağıtımda', 'Teslim Edildi'];
    const statusMap: Record<string, number> = { beklemede: 1, hazirlaniyor: 2, kargoda: 3, dagitimda: 4, teslim_edildi: 5 };
    const current = statusMap[durum] || 1;
    return allSteps.map((step, i) => ({ status: step, completed: i < current, description: i < current ? 'Tamamlandı' : 'Bekleniyor' }));
  };

  if (!showResults) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="max-w-2xl w-full">
            <div className="text-center mb-8">
              <div className="inline-flex size-24 bg-pink-100 rounded-full items-center justify-center mb-6"><Package className="size-12 text-pink-600" /></div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Sipariş Takip</h1>
              <p className="text-xl text-gray-600">Siparişinizin durumunu öğrenmek için sipariş numaranızı giriniz</p>
            </div>
            <form onSubmit={handleTrack} className="bg-white rounded-2xl shadow-xl p-8">
              <label className="block text-sm font-semibold text-gray-900 mb-3">Sipariş Numarası</label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                  <input type="text" value={trackingInput} onChange={(e) => setTrackingInput(e.target.value)} placeholder="Sipariş numaranız" className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500 text-lg" />
                </div>
                <button type="submit" disabled={loading} className="bg-pink-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-pink-700 transition-colors whitespace-nowrap disabled:bg-gray-400">
                  {loading ? '...' : 'Sorgula'}
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-4">Sipariş numaranızı sipariş onay e-postanızdan veya SMS'inizden bulabilirsiniz.</p>
            </form>
            <div className="mt-8 bg-white rounded-xl p-6 shadow-lg">
              <h3 className="font-semibold text-gray-900 mb-4">Sipariş numaranızı bulamıyor musunuz?</h3>
              <div className="space-y-3 text-sm text-gray-700">
                <p>• Sipariş onay e-postanızı kontrol edin</p>
                <p>• Hesabım &gt; Siparişlerim bölümünden görüntüleyin</p>
              </div>
              <div className="mt-4 pt-4 border-t flex gap-4">
                <a href="tel:+905434671896" className="flex items-center gap-2 text-pink-600 hover:text-pink-700 font-medium"><Phone className="size-4" />Bizi Arayın</a>
                <a href="/iletisim" className="flex items-center gap-2 text-pink-600 hover:text-pink-700 font-medium"><Mail className="size-4" />İletişime Geçin</a>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const steps = getSteps(orderData?.durum || 'beklemede');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 mb-1">Sipariş Takip: #{orderData?.id}</h1>
                <p className="text-gray-600">Sipariş Tarihi: {orderData?.created_at ? new Date(orderData.created_at).toLocaleDateString('tr-TR') : '-'}</p>
              </div>
              <button onClick={() => { setShowResults(false); setOrderData(null); }} className="flex items-center gap-2 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors">
                <Search className="size-5" />Yeni Sorgulama
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Current Status Banner */}
              <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="size-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm"><Truck className="size-8" /></div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold mb-1">{getDurumText(orderData?.durum || 'beklemede')}</h2>
                    <p className="text-green-50">Sipariş durumunuz yukarıda gösterilmektedir</p>
                  </div>
                </div>
              </div>

              {/* Tracking Steps */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Sipariş Durumu</h2>
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  <div className="space-y-6">
                    {steps.map((step, index) => (
                      <div key={index} className="relative flex gap-4">
                        <div className={`relative z-10 size-8 rounded-full flex items-center justify-center flex-shrink-0 ${step.completed ? 'bg-green-500' : 'bg-gray-300'}`}>
                          {step.completed ? <CheckCircle2 className="size-5 text-white" /> : <div className="size-3 bg-white rounded-full"></div>}
                        </div>
                        <div className="flex-1 pb-6">
                          <h3 className={`font-semibold ${step.completed ? 'text-gray-900' : 'text-gray-400'}`}>{step.status}</h3>
                          <p className={`text-sm ${step.completed ? 'text-gray-600' : 'text-gray-400'}`}>{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Order Items */}
              {orderData?.urunler && orderData.urunler.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Sipariş İçeriği</h2>
                  <div className="space-y-4">
                    {orderData.urunler.map((item: any, index: number) => (
                      <div key={index} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                        {item.resimler?.[0] && <img src={item.resimler[0]} alt={item.ad} className="size-20 object-cover rounded" />}
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{item.ad}</h3>
                          <p className="text-sm text-gray-600 mb-2">{item.beden && `Beden: ${item.beden}`} • Adet: {item.adet}</p>
                          <p className="text-lg font-bold text-pink-600">₺{Number(item.fiyat).toLocaleString('tr-TR')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Sipariş Bilgileri</h3>
                <div className="space-y-3 text-sm">
                  <div><span className="text-gray-600">Toplam Tutar:</span><p className="font-semibold text-pink-600">₺{Number(orderData?.toplam_tutar || 0).toLocaleString('tr-TR')}</p></div>
                  <div><span className="text-gray-600">Ödeme Tipi:</span><p className="font-semibold text-gray-900">{orderData?.odeme_tipi || '-'}</p></div>
                  {orderData?.adres && <div><span className="text-gray-600">Adres:</span><p className="font-semibold text-gray-900">{orderData.adres}</p></div>}
                </div>
              </div>
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Yardıma mı ihtiyacınız var?</h3>
                <p className="text-sm text-gray-700 mb-4">Siparişiniz hakkında sorularınız için bizimle iletişime geçin.</p>
                <div className="space-y-2">
                  <a href="tel:+905434671896" className="flex items-center gap-2 text-pink-600 hover:text-pink-700 font-medium text-sm"><Phone className="size-4" />+90 543 467 18 96</a>
                  <a href="/iletisim" className="flex items-center gap-2 text-pink-600 hover:text-pink-700 font-medium text-sm"><Mail className="size-4" />İletişim Formu</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
